import React, { useCallback, useEffect, useState } from 'react';
import { TSession } from './Sessions';
import { withRouter, RouteComponentProps } from 'react-router';
import { Tooltip } from '@material-ui/core';
import { Input, CheckCircle, Error } from '@material-ui/icons';
import { LinkDiv } from './SessionPrepareButton';
import { CircularProgressWithLabel } from './SessionUploadButton';
import { RunState, RunExportStatus } from '../../api';
import { sessionRunStates } from '../../services/jobs';
import useSessionJobs, { toPercent } from './useSessionJobs';

interface Props extends RouteComponentProps {
  session: TSession;
}

type Progress = {
  save: number,
  export: number
}

const SessionLabelButton: React.FunctionComponent<Props> = ({ session, history }) => {
  const onClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      history.push('/session/label/' + session.id);
    },
    [session, history]
  );
  const [runExportStatus, setRunExportStatus] =  useState<string | null | undefined>(session.lastRun?.exportStatus);

  const lastRunId = session.lastRun?.id || "-1";
  const labelingJob = useSessionJobs(String(session.id)).find(
    (job) => job.type === 'labeling' && job.runId === session.lastRun?.id && job.progress != null
  );

  const [progress, setProgress] = useState<Progress>({ 
    save: 0, 
    export: 0
  });

  useEffect(() => {
    sessionRunStates.addHandler(lastRunId, run => {
      if (run?.exportJob != null && session?.lastRun?.exportStatus === RunExportStatus.SavingRun) {
        setRunExportStatus(RunExportStatus.Exporting);
      }

      setProgress({
        save: (run?.saveJob?.progress || 0) * 100,
        export: (run?.exportJob?.progress || 0) * 100
      })
    });

    return () => {
      sessionRunStates.removeHandler(lastRunId)
    }
  }, [lastRunId, session])

  //Enable go-to button for labeling.
  if (
    (
      session.lastRun?.state === RunState.Stopped
      || session.lastRun?.state === RunState.PostProcess
    ) &&
    !session.lastRun?.errorMessage &&
    session.labelTasks &&
    session.labelTasks.length > 0
  ) {
    if (runExportStatus === RunExportStatus.NotStarted) {
      return (
        <LinkDiv onClick={onClick}>
          <Tooltip title="Go to track labeling.">
            {labelingJob ? (
              <CircularProgressWithLabel color="secondary" value={toPercent(labelingJob)} />
            ) : (
              <Input color="secondary" />
            )}
          </Tooltip>
        </LinkDiv>
      );
    } else if (runExportStatus === RunExportStatus.Failed) {
      return (
        <LinkDiv onClick={onClick}>
          <Tooltip title="EPTS export failed">
            <Error color="error" />
          </Tooltip>
        </LinkDiv>
      );
    } else if (runExportStatus === RunExportStatus.Exported) {
      return (
        <LinkDiv onClick={onClick}>
          <Tooltip title="Labeled data has been exported">
            <CheckCircle color="secondary" />
          </Tooltip>
        </LinkDiv>
      );
    } else if (runExportStatus === RunExportStatus.SavingRun) {
      return (
        <LinkDiv onClick={onClick}>
            <Tooltip title="Saving labeled data">
            <CircularProgressWithLabel
              color="secondary"
              value={progress.save}
            />
          </Tooltip>
          <div className="sublabel">Saving data</div>
        </LinkDiv>
      );
    } else if (runExportStatus === RunExportStatus.Exporting) {
      return (
        <LinkDiv onClick={onClick}>
          <Tooltip title="Exporting labeled data to EPTS">
            <CircularProgressWithLabel color="secondary" value={progress.export} />
          </Tooltip>
          <div className="sublabel">Exporting data</div>
        </LinkDiv>
      );
    }
  }

  return <div />;
};

export default withRouter(SessionLabelButton);
